import React from 'react';
import { ArrowLeft } from 'lucide-react';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-heading text-xl font-semibold text-primary mb-4">{title}</h2>
    {children}
  </section>
);

const Para = ({ children }) => (
  <p className="text-text-main text-sm leading-relaxed mb-3">{children}</p>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5 ml-1 mb-3">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-text-main text-sm leading-relaxed">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Email = ({ address }) => (
  <a href={`mailto:${address}`} className="text-primary hover:underline">{address}</a>
);

const PageLink = ({ onNavigate, to, href, children }) => (
  <a
    href={href}
    onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate(to); } }}
    className="text-primary hover:underline"
  >
    {children}
  </a>
);

const Plan = ({ name, price, per, note }) => (
  <div className="rounded-arch-lg border border-teal/25 bg-white/60 dark:bg-white/5 p-5 text-center">
    <p className="text-sm text-text-muted">{name}</p>
    <p className="font-heading text-3xl font-semibold text-text-main mt-1">{price}</p>
    <p className="text-xs text-text-muted mt-0.5">{per}</p>
    <p className="text-xs text-text-main mt-3 leading-relaxed">{note}</p>
  </div>
);

/** Public, no-login page: who Sirati is, what it sells and for how much, and how to reach and get refunds from us. */
const AboutPage = ({ onNavigate }) => (
  <div className="min-h-screen bg-background" data-testid="about-page">
    <div className="h-1 w-full" style={{ background: 'linear-gradient(to right, #4AADA8, #A99CC8)' }} aria-hidden="true" />

    <div className="max-w-2xl mx-auto px-6 py-12">
      {onNavigate && (
        <button
          onClick={() => onNavigate('feed')}
          aria-label="Back to Sirati"
          className="flex items-center gap-2 text-sm text-text-muted hover:text-primary transition-colors mb-10 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Back to Sirati
        </button>
      )}

      <header className="text-center mb-12">
        <p className="font-heading text-3xl font-bold tracking-wide text-primary mb-1">SIRATI</p>
        <h1 className="font-heading text-2xl font-semibold mb-3" style={{ color: '#A99CC8' }}>About Sirati</h1>
        <p className="text-text-main text-base leading-relaxed">
          A faith-centered community for Muslim singles preparing for marriage with intention.
        </p>
      </header>

      <Section title="What Sirati is">
        <Para>
          Sirati is an online learning and community platform for Muslim singles (18 and over) focused on personal growth and marriage readiness. Members learn through courses we call Spaces, follow their progress on a personal dashboard, take part in community discussion, and attend in-person and online events.
        </Para>
        <Para>
          Sirati is a community and learning platform. We do not run background checks on members and we do not promise any particular outcome. See our <PageLink onNavigate={onNavigate} to="terms" href="/terms">Terms &amp; Conditions</PageLink> for details.
        </Para>
      </Section>

      <Section title="What members get for free">
        <BulletList items={[
          'A community feed to share, ask questions, and encourage others',
          'Learning Spaces: Know Yourself opens on November 1, 2026, with more Spaces coming soon',
          'A personal dashboard with a Marriage Readiness tracker, a salah tracker, goals, and badges',
          'Free guides and tools in the Library, such as "How Muslim Couples Met"',
          'Registration for in-person and online events (events have their own ticket prices, shown below)',
        ]} />
      </Section>

      <Section title="Sirati+ membership">
        <Para>
          Sirati+ is our paid membership. It adds member messaging, profile personalization, early access to event tickets, and a discount on event tickets. All prices are in US dollars.
        </Para>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <Plan name="Monthly" price="$7" per="per month" note="Renews automatically each month until you cancel." />
          <Plan name="Yearly" price="$70" per="per year" note="Renews automatically each year until you cancel. Saves 17% compared with monthly." />
          <Plan name="Lifetime" price="$97" per="one-time payment" note="Founding-member price. Does not renew; nothing to cancel." />
        </div>
        <BulletList items={[
          'Message other members you connect with',
          'Personalize your profile with social links, wallpapers, media, and widgets',
          'Early access to event tickets and 10% off every event ticket',
          'Live expert sessions and partner perks, as they become available',
        ]} />
        <Para>Prices shown are our current beta pricing for early members.</Para>
      </Section>

      <Section title="Events">
        <Para>
          We host in-person events with partner venues and online events. Tickets are typically $25 to $50, and each event page shows the exact price, date, city, and number of spots before you pay. The exact address or online link is shown after you register. Some events are free.
        </Para>
      </Section>

      <Section title="Library">
        <Para>
          The Library has free guides and tools for every member, extra resources that come with Sirati+, and some resources sold separately as one-time purchases. Each item shows its price before you pay. Sirati+ members get 50% off items sold separately, and Lifetime members get every item included. Resources are digital downloads for personal use.
        </Para>
      </Section>

      <Section title="Billing, cancellation, and refunds">
        <BulletList items={[
          'Payments are processed securely by Stripe. We do not store your card details.',
          'Cancellation: monthly and yearly Sirati+ renew automatically. You can cancel any time online from your Profile in a few clicks, with no email or phone call needed. Cancellation takes effect at the end of your current billing period, and you keep access until then. Lifetime Sirati+ is a one-time payment, so there is nothing to cancel.',
          'Refunds for Sirati+: except where the law requires otherwise, Sirati+ payments are non-refundable and we do not give partial refunds for unused time.',
          'Refunds for Library purchases: a Library purchase is a one-time payment. It is non-refundable once you have downloaded it. If you have not downloaded it, you can ask for a full refund within 7 days by emailing salam@sirati.co.',
          'Refunds for event tickets: you can cancel and get a full refund of your ticket price up to 72 hours before the event starts. After that there is no refund. If we cancel an event, you get a full refund.',
          'Disputes and billing questions: if you think you were charged in error, or you do not recognize a charge, please email us before contacting your bank. We reply within 1 to 3 business days and will fix any mistake promptly.',
        ]} />
        <Para>
          The full terms are in Section 7 (Paid Features and Billing) and <PageLink onNavigate={onNavigate} to="terms" href="/terms#events">Section 20 (Events)</PageLink> of our <PageLink onNavigate={onNavigate} to="terms" href="/terms">Terms &amp; Conditions</PageLink>.
        </Para>
      </Section>

      <Section title="Contact us">
        <div className="p-4 rounded-arch-lg border border-teal/25 bg-teal/5 text-sm text-text-main space-y-1">
          <p><span className="font-medium text-primary">Sirati customer support</span></p>
          <p>Email: <Email address="salam@sirati.co" /> (we reply within 1 to 3 business days)</p>
          <p>Privacy requests: <Email address="privacy@sirati.co" /></p>
          <p>Website: <a href="https://www.sirati.co" className="text-primary hover:underline">www.sirati.co</a></p>
          <p>Mailing address: 405 E Laburnum Ave Ste 3, Richmond, VA 23222</p>
        </div>
      </Section>

      <nav aria-label="Legal and policy pages" className="mt-12 pt-8 border-t border-border text-center">
        <p className="text-sm text-text-muted space-x-1">
          <PageLink onNavigate={onNavigate} to="terms" href="/terms">Terms &amp; Conditions</PageLink>
          <span aria-hidden="true">&middot;</span>
          <PageLink onNavigate={onNavigate} to="privacy" href="/privacy">Privacy Policy</PageLink>
          <span aria-hidden="true">&middot;</span>
          <PageLink onNavigate={onNavigate} to="child-safety" href="/child-safety">Child Safety</PageLink>
          <span aria-hidden="true">&middot;</span>
          <PageLink onNavigate={onNavigate} to="delete-data" href="/delete-data">Delete My Data</PageLink>
        </p>
        {onNavigate && (
          <button
            onClick={() => onNavigate('feed')}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white transition-opacity hover:opacity-90 min-h-[44px]"
            style={{ background: 'linear-gradient(135deg, #4AADA8, #A99CC8)' }}
          >
            Create My Free Account
          </button>
        )}
        <p className="text-xs text-text-muted mt-6">&copy; {new Date().getFullYear()} Sirati. All Rights Reserved.</p>
      </nav>
    </div>
  </div>
);

export default AboutPage;
